import React, { useState, useEffect, useRef } from "react";
import { Box, Flex, Heading, Text } from "@theme-ui/components";
import { keyframes } from "@emotion/react";
import { navigate } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";
import { Button, Icon } from "../atoms";

const PersonaSection = ({ personas, heading, description, variant, pageName, regionCode }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const tabsContainerRef = useRef(null);
    const [isProgressing, setIsProgressing] = useState(false);
    const [isSelectedTab, setIsSelectedTab] = useState(false);
    const [showArrows, setShowArrows] = useState(false);
    const progressAnimation = keyframes`
    from { width: 0%; }
    to { width: 100%; }
  `;

    const scrollToSelectedTab = () => {
        const tabElement = tabsContainerRef.current?.children[selectedTab];
        if (tabElement) {
            const tabWidth = tabElement.offsetWidth;
            const containerWidth = tabsContainerRef.current.offsetWidth;
            const offset = (containerWidth - tabWidth) / 2;

            tabsContainerRef.current.scrollTo({
                left: tabElement.offsetLeft - offset,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        scrollToSelectedTab();
    }, [selectedTab]);

    const handleLeftArrowClick = () => {
        if (tabsContainerRef.current) {
            const scrollLeft = tabsContainerRef.current.scrollLeft;
            const tabWidth = tabsContainerRef.current.children[0].offsetWidth;
            tabsContainerRef.current.scrollTo({
                left: scrollLeft - tabWidth,
                behavior: 'smooth',
            });

            setSelectedTab((prev) => (prev > 0 ? prev - 1 : prev));
        }
    };

    const handleRightArrowClick = () => {
        if (tabsContainerRef.current) {
            const scrollLeft = tabsContainerRef.current.scrollLeft;
            const tabWidth = tabsContainerRef.current.children[0].offsetWidth;
            tabsContainerRef.current.scrollTo({
                left: scrollLeft + tabWidth,
                behavior: 'smooth',
            });

            setSelectedTab((prev) =>
                prev < personas?.length - 1 ? prev + 1 : prev
            );
        }
    };

    useEffect(() => {
        let timer;
        if (isProgressing) {
            timer = setTimeout(() => {
                setIsProgressing(false);
                setSelectedTab((prevTab) => (prevTab + 1) % personas?.length);
                setIsSelectedTab(false);
            }, isSelectedTab ? 55000 : 8000);
            return () => clearTimeout(timer);
        }
    }, [isProgressing, selectedTab, isSelectedTab, personas?.length]);

    useEffect(() => {
        setIsProgressing(true);
    }, [selectedTab]);

    const handleTabClick = (tabIndex) => {
        setSelectedTab(tabIndex);
        setIsSelectedTab(true);
        setIsProgressing(false);
    };

    useEffect(() => {
        const checkTabWidth = () => {
            const containerWidth = tabsContainerRef.current?.offsetWidth || 0;
            const windowWidth = window.innerWidth;
            if (windowWidth > 1024) {
                setShowArrows(containerWidth > 1056);
            } else {
                setShowArrows(true);
            }
        };
        if (tabsContainerRef.current) {
            checkTabWidth();
        }
        window.addEventListener('resize', checkTabWidth);
        return () => window.removeEventListener('resize', checkTabWidth);
    }, []);

    if (!variant || variant === "variant1") {
        return (
            <Box sx={{ mx: ["1rem", "1rem", "2rem", "auto"] }}>
                <Heading sx={{ ...typography.h2, textAlign: "center", p: { m: 0 }, mb: "1.5rem" }} dangerouslySetInnerHTML={{ __html: heading }} />
                <Text sx={{ ...typography.textMd400, textAlign: "center", p: { m: 0 } }} dangerouslySetInnerHTML={{ __html: description }} />
                <Flex sx={mobileTabContainerStyles}>
                    {showArrows && (
                        <>
                            <Flex
                                sx={{
                                    ...tabLeftContainer,
                                    bottom: personas?.some(persona => persona.image?.gatsbyImageData) ? '2%' : '7%',
                                    height: personas?.some(persona => persona.image?.gatsbyImageData) ? '10rem' : '5rem'
                                }}
                                onClick={handleLeftArrowClick}
                            >
                                <Icon name="ChevronLeft" size={32} color={colors.primaryMain} />
                            </Flex>
                            <Flex
                                sx={{
                                    ...tabRightContainer,
                                    bottom: personas?.some(persona => persona.image?.gatsbyImageData) ? '2%' : '7%',
                                    height: personas?.some(persona => persona.image?.gatsbyImageData) ? '10rem' : '5rem'
                                }}
                                onClick={handleRightArrowClick}
                            >
                                <Icon name="ChevronRight" size={32} color={colors.primaryMain} />
                            </Flex>
                        </>
                    )}
                    <Flex
                        ref={tabsContainerRef}
                        sx={tabContainerStyles}
                    >
                        {personas?.map((persona, index) => (
                            <Box
                                key={persona.title}
                                sx={{
                                    ...personaTabStyles,
                                    ...(persona.image?.gatsbyImageData
                                        ? { width: '9.063rem' }
                                        : { minWidth: '9.063rem', px: '1rem' }),
                                    border: `2px solid ${selectedTab === index ? colors.primaryMain : colors.neutral200}`,
                                    opacity: 1,
                                    cursor: 'pointer',
                                    "@media (min-width: 1025px)": {
                                        "&:hover": {
                                            borderColor: colors.primaryMain,
                                        },
                                        "&:hover .persona-title": {
                                            color: colors.primary700,
                                        },
                                    },
                                }}
                                onClick={() => handleTabClick(index)}
                            >
                                {selectedTab === index && (
                                    <Box sx={{ ...selectedTabStyles, animation: `${progressAnimation} ${isSelectedTab ? '55s' : '8s'} linear infinite` }} />
                                )}
                                {persona.image?.gatsbyImageData && (
                                    <GatsbyImage
                                        image={getImage(persona.image.gatsbyImageData)}
                                        alt={persona.image.alt}
                                        sx={imageStylesV1}
                                    />
                                )}
                                <Heading className="persona-title" sx={{ ...typography.h6, ...(persona.image?.gatsbyImageData && { mt: '1rem' }), position: 'relative', zIndex: 0, color: selectedTab === index ? colors.primary700 : colors.neutral900, }}>
                                    {persona.title}
                                </Heading>
                            </Box>
                        ))}
                    </Flex>
                </Flex>
                {selectedTab !== null && (
                    <Box sx={{ mt: "2rem" }}>
                        <Box>
                            {personas[selectedTab]?.sections?.map((section, index) => (
                                <Box
                                    key={`tab-section-${index}`}
                                    sx={{
                                        ...sectionStyles,
                                        backgroundColor: colors[`${section?.color}100`],
                                        ...(index === 0 && { border: `1px solid ${colors.neutral200}`, mb: "1.5rem" }),
                                        ...(index === 1 && { py: "0.75rem", mb: "1.5rem" }),
                                        ...(index === 2 && { pt: "1.5rem" }),
                                    }}
                                >
                                    <Heading
                                        sx={{ ...typography.h5, mt: "1.5rem", color: colors.neutral900 }}
                                        dangerouslySetInnerHTML={{ __html: section?.heading }}
                                    />
                                    <Text
                                        sx={listStyles(section?.bulletColor)}
                                        dangerouslySetInnerHTML={{ __html: section?.description }}
                                    />
                                    {section?.image && (
                                        <GatsbyImage
                                            image={getImage(section?.image.gatsbyImageData)}
                                            alt={section?.heading}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Flex sx={desktopSectionStylesV1}>
                            <Flex
                                sx={{
                                    flex: "1",
                                    "@media(min-width: 1025px)": {
                                        mr: "2rem",
                                    },
                                    "@media screen and (min-width: 900px) and (max-width: 1024px)": {
                                        mb: "1.5rem",
                                    },
                                    flexDirection: "column",
                                }}
                            >
                                {personas[selectedTab]?.sections?.slice(0, 2).map((section, index) => (
                                    <Flex
                                        key={`desktop-section-${index}`}
                                        sx={{
                                            mb: index === 0 ? "1.5rem" : "0",
                                            backgroundColor: colors[`${section?.color}100`],
                                            ...(index === 0 && { border: `1px solid ${colors.neutral200}` }),
                                            ...featureDescriptionV1,
                                        }}
                                    >
                                        <Heading
                                            sx={{ ...typography.h5, mt: "1.5rem", p: { m: 0 }, color: colors.neutral900 }}
                                            dangerouslySetInnerHTML={{ __html: section?.heading }}
                                        />
                                        <Box
                                            sx={listStyles(section?.bulletColor)}
                                            dangerouslySetInnerHTML={{ __html: section?.description }}
                                        />
                                    </Flex>
                                ))}
                            </Flex>
                            {personas[selectedTab]?.sections[2] && (
                                <Flex
                                    sx={{
                                        ...featureDescriptionV1,
                                        backgroundColor: colors[`${personas[selectedTab]?.sections[2]?.color}100`],
                                    }}
                                >
                                    {personas[selectedTab]?.sections[2]?.image && (
                                        <Box sx={{ mt: "2rem" }}>
                                            <GatsbyImage
                                                image={getImage(personas[selectedTab]?.sections[2].image.gatsbyImageData)}
                                                alt={personas[selectedTab]?.sections[2].heading}
                                                style={{ width: "100%", height: "auto" }}
                                            />
                                        </Box>
                                    )}
                                </Flex>
                            )}
                        </Flex>
                    </Box>
                )}
                <Flex sx={{ justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                    {selectedTab !== null && personas[selectedTab] && (
                        (personas[selectedTab]?.primaryCtaName || personas[selectedTab]?.secondaryCtaName) && (
                            <Flex
                                sx={{
                                    mt: ["3rem", "3rem", "3rem", "4rem"],
                                    alignItems: "center",
                                    flexDirection: ["column", "column", "row"],
                                    gap: ["1.5rem", "1.5rem", "3rem", "3rem"],
                                }}
                            >
                                {personas[selectedTab]?.primaryCtaName && personas[selectedTab]?.primaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="filled"
                                        id={`${pageName.toLowerCase()}_persona_variant1_primary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() =>
                                            window.open(
                                                personas[selectedTab].primaryCtaUrl,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        customStyles={{
                                            width: "max-content",
                                        }}
                                    >
                                        {personas[selectedTab]?.primaryCtaName}
                                    </Button>
                                )}
                                {personas[selectedTab]?.secondaryCtaName && personas[selectedTab]?.secondaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="outlined"
                                        id={`secondary-cta-${selectedTab}`}
                                        onClick={() =>
                                            window.open(
                                                personas[selectedTab]?.secondaryCtaUrl,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        customStyles={{
                                            width: "max-content",
                                        }}
                                    >
                                        {personas[selectedTab]?.secondaryCtaName}
                                    </Button>
                                )}
                            </Flex>
                        )
                    )}
                </Flex>
            </Box>
        );
    } else if (variant === "variant2") {
        return (
            <Box sx={{ mx: ["1.063rem", "1.063rem", "1.375rem", "auto"] }}>
                <Heading sx={{ ...typography.h2, textAlign: "center", p: { m: 0 }, mb: "1.5rem", mx: "2rem" }} dangerouslySetInnerHTML={{ __html: heading }} />
                <Box sx={{ maxWidth: ["100%", "100%", "100%", "60%"], margin: "0 auto", textAlign: "center" }}>
                    <Text sx={{ ...typography.textMd400, textAlign: "center", p: { m: 0 } }} dangerouslySetInnerHTML={{ __html: description }} />
                </Box>
                <Flex sx={mobileTabContainerStyles}>
                    {showArrows && (
                        <>
                            <Flex
                                sx={{
                                    ...tabLeftContainer,
                                    bottom: personas?.some(persona => persona.image?.gatsbyImageData) ? '2%' : '7%',
                                    height: personas?.some(persona => persona.image?.gatsbyImageData) ? '10rem' : '5rem'
                                }}
                                onClick={handleLeftArrowClick}
                            >
                                <Icon name="ChevronLeft" size={32} color={colors.primaryMain} />
                            </Flex>
                            <Flex
                                sx={{
                                    ...tabRightContainer,
                                    bottom: personas?.some(persona => persona.image?.gatsbyImageData) ? '2%' : '7%',
                                    height: personas?.some(persona => persona.image?.gatsbyImageData) ? '10rem' : '5rem'
                                }}
                                onClick={handleRightArrowClick}
                            >
                                <Icon name="ChevronRight" size={32} color={colors.primaryMain} />
                            </Flex>
                        </>
                    )}
                    <Flex
                        ref={tabsContainerRef}
                        sx={tabContainerStyles}
                    >
                        {personas?.map((persona, index) => (
                            <Box
                                key={persona.title}
                                sx={{
                                    ...personaTabStyles,
                                    ...(persona.image?.gatsbyImageData
                                        ? { width: '9.063rem' }
                                        : { minWidth: '9.063rem', px: '1rem' }),
                                    border: `2px solid ${selectedTab === index ? colors.primaryMain : colors.neutral200}`,
                                    opacity: 1,
                                    cursor: 'pointer',
                                    "@media (min-width: 1025px)": {
                                        "&:hover": {
                                            borderColor: colors.primaryMain,
                                        },
                                        "&:hover .persona-title": {
                                            color: colors.primary700,
                                        },
                                    },
                                }}
                                onClick={() => handleTabClick(index)}
                            >
                                {selectedTab === index && (
                                    <Box
                                        sx={{
                                            ...selectedTabStyles,
                                            animation: `${progressAnimation} ${isSelectedTab ? '55s' : '8s'} linear infinite`,
                                        }}
                                    />
                                )}
                                {persona.image?.gatsbyImageData && (
                                    <GatsbyImage
                                        image={getImage(persona.image.gatsbyImageData)}
                                        alt={persona.image.alt}
                                        sx={imageStylesV1}
                                    />
                                )}
                                <Heading className="persona-title" sx={{ ...typography.h6, ...(persona.image?.gatsbyImageData && { mt: '1rem' }), position: 'relative', zIndex: 0, color: selectedTab === index ? colors.primary700 : colors.neutral900, }}>
                                    {persona.title}
                                </Heading>
                            </Box>
                        ))}
                    </Flex>
                </Flex>
                {selectedTab !== null && (
                    <Box sx={{ mt: ["3rem", "3rem", "2rem", "2rem"] }}>
                        <Box>
                            {[personas[selectedTab]?.sections[1], personas[selectedTab]?.sections[0]]?.map((section, index) => (
                                <Box
                                    key={`tab-section-${index}`}
                                    sx={{
                                        ...sectionStyles,
                                        backgroundColor: colors[`${section?.color}100`],
                                        ...(index === 0 && { pt: "1.5rem", mb: "1.5rem" }),
                                        ...(index === 1 && { border: `1px solid ${colors.neutral200}` }),
                                    }}
                                >
                                    {section?.image && (
                                        <Flex
                                            sx={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                "@media screen and (min-width: 400px) and (max-width: 899px)": {
                                                    height: "19.125rem",
                                                },
                                            }}
                                        >
                                            <GatsbyImage
                                                image={getImage(section?.image.gatsbyImageData)}
                                                alt={section?.heading}
                                                style={{ height: "100%", }}
                                                imgStyle={{ objectFit: "contain" }}
                                            />
                                        </Flex>
                                    )}
                                    <Heading
                                        sx={{ ...typography.h5, mt: "1.5rem", color: colors.neutral900 }}
                                        dangerouslySetInnerHTML={{ __html: section?.heading }}
                                    />
                                    <Text
                                        sx={listStyles(section?.bulletColor)}
                                        dangerouslySetInnerHTML={{ __html: section?.description }}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <Flex sx={desktopSectionStylesV2}>
                            {personas[selectedTab]?.sections?.map((section, index) => (
                                <Box
                                    key={`desktop-section-${index}`}
                                    sx={{
                                        ...featureDescriptionV2,
                                        backgroundColor: colors[`${section?.color}100`],
                                        ...(index === 0 && { border: `1px solid ${colors.neutral200}`, mr: "1rem", maxWidth: "46.313rem" }),
                                        ...(index === 1 && { pt: "1.5rem", maxWidth: "26.125rem" }),
                                    }}
                                >
                                    {index === 0 ? (
                                        <Box>
                                            <Heading
                                                sx={{ ...typography.h5, mt: "1.5rem", color: colors.neutral900 }}
                                                dangerouslySetInnerHTML={{ __html: section?.heading }}
                                            />
                                            <Text
                                                sx={listStyles(section?.bulletColor)}
                                                dangerouslySetInnerHTML={{ __html: section?.description }}
                                            />
                                        </Box>
                                    ) : index === 1 ? (
                                        <Flex
                                            sx={{
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: "100%",
                                                width: "100%",
                                            }}
                                        >
                                            <GatsbyImage
                                                image={getImage(section?.image?.gatsbyImageData)}
                                                alt={section?.heading}
                                                style={{ objectFit: "contain" }}
                                            />
                                        </Flex>
                                    ) : null}
                                </Box>
                            ))}
                        </Flex>
                    </Box>
                )}
                <Flex sx={{ justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                    {selectedTab !== null && personas[selectedTab] && (
                        (personas[selectedTab].primaryCtaName || personas[selectedTab].secondaryCtaName) && (
                            <Flex
                                sx={{
                                    mt: ["3rem", "3rem", "3rem", "4rem"],
                                    alignItems: "center",
                                    flexDirection: ["column", "column", "row"],
                                    gap: ["1.5rem", "1.5rem", "3rem", "3rem"],
                                }}
                            >
                                {personas[selectedTab].primaryCtaName && personas[selectedTab].primaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="filled"
                                        id={`${pageName.toLowerCase()}_persona_variant2_primary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() =>
                                            window.open(
                                                personas[selectedTab].primaryCtaUrl,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        customStyles={{
                                            width: "max-content",
                                        }}
                                    >
                                        {personas[selectedTab].primaryCtaName}
                                    </Button>
                                )}
                                {personas[selectedTab].secondaryCtaName && personas[selectedTab].secondaryCtaUrl && (
                                    <Button
                                        size="large"
                                        variant="outlined"
                                        id={`${pageName.toLowerCase()}_persona_variant2_secondary_${regionCode.toLowerCase()}_btn`}
                                        onClick={() =>
                                            window.open(
                                                personas[selectedTab].secondaryCtaUrl,
                                                "_blank",
                                                "noopener,noreferrer"
                                            )
                                        }
                                        customStyles={{
                                            width: "max-content",
                                        }}
                                    >
                                        {personas[selectedTab].secondaryCtaName}
                                    </Button>
                                )}
                            </Flex>
                        )
                    )}
                </Flex>
            </Box>
        );
    }
};

const listStyles = (bulletColor) => ({
    ...typography.textMd400,
    color: colors.neutral900,
    "p": {
        ...typography.textMd500,
        color: colors.neutral900,
    },
    "h5": {
        ...typography.h5,
        color: colors.neutral900,
    },
    "h6": {
        ...typography.h6,
        display: "inline",
        color: colors.neutral900,
    },
    'p1': {
        ...typography.textMd400,
        display: 'inline',
        color: colors.neutral900,
        margin: 0,
    },
    "& ul": {
        listStyle: "none",
        pl: "0",
        ml: "0",
    },
    "& li": {
        position: "relative",
        mb: ["0.75rem", "0.75rem", "1rem", "1rem"],
        pl: "1.5rem",
        "::before": {
            content: '""',
            display: "inline-block",
            width: "0.875rem",
            height: "0.875rem",
            backgroundColor: colors[`${bulletColor}500`],
            borderRadius: "50%",
            position: "absolute",
            left: "0",
            top: "0.4rem",
        },
    },
});

const {
    featureDescriptionV1,
    imageStylesV1,
    desktopSectionStylesV1,
    featureDescriptionV2,
    personaTabStyles,
    desktopSectionStylesV2,
} = {
    featureDescriptionV1: {
        borderRadius: "0.75rem",
        px: "2rem",
        flex: "1",
        flexDirection: "column",
        maxWidth: "34.875rem",
        height: "100%",
    },
    imageStylesV1: {
        position: 'relative',
        zIndex: 1,
        width: '80%',
        height: 'auto',
    },
    desktopSectionStylesV1: {
        justifyContent: "space-between",
        alignItems: "stretch",
        "@media (max-width: 899px)": {
            display: "none",
        },
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            flexDirection: "column",
            alignItems: "center",
        },
    },
    featureDescriptionV2: {
        borderRadius: "0.75rem",
        px: "2rem",
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    personaTabStyles: {
        flexShrink: 0,
        paddingY: '1.063rem',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        scrollSnapAlign: 'center',
        borderRadius: '0.5rem',
        boxSizing: 'border-box',
    },
    desktopSectionStylesV2: {
        "@media (max-width: 899px)": {
            display: "none",
        },
        justifyContent: "center",
        alignItems: "stretch",
        mx: "2rem",
    },
};

const {
    sectionStyles,
    tabLeftContainer,
    tabRightContainer,
    tabContainerStyles,
    mobileTabContainerStyles,
    selectedTabStyles
} = {
    sectionStyles: {
        flexDirection: "column",
        px: "2rem",
        mx: ["1rem", "1rem", "2rem", "auto"],
        borderRadius: "0.75rem",
        height: "100%",
        display: "none",
        "@media (max-width: 899px)": {
            display: "block",
        },
    },
    tabLeftContainer: {
        width: "5rem",
        position: "absolute",
        left: "-2rem",
        zIndex: 1,
        justifyContent: "center",
        alignItems: "center",
        background:
            "linear-gradient(to right, #ffffff, #ffffff, #ffffff, transparent)",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            ml: "1.45rem",
        },
    },
    tabRightContainer: {
        width: "5rem",
        position: "absolute",
        right: "-2rem",
        zIndex: 1,
        justifyContent: "center",
        alignItems: "center",
        background:
            "linear-gradient(to left, #ffffff, #ffffff, #ffffff, #ffffff, transparent)",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            mr: "1.45rem",
        },
    },
    tabContainerStyles: {
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        padding: '1rem 0 1rem 0',
        marginTop: '3rem',
        gap: '2rem',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        justifyContent: 'flex-start',
        alignItems: 'center',
        mx: "auto",
        "@media screen and (min-width: 900px) and (max-width: 1024px)": {
            maxWidth: "52.625rem"
        },
    },
    mobileTabContainerStyles: {
        position: 'relative',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    selectedTabStyles: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        backgroundColor: colors.primary100,
        zIndex: 0,
        inset: 0,
        borderRadius: '0.5rem',
        boxSizing: 'border-box',
    },
}

export default PersonaSection;